"use client";

import React, { useEffect, useRef, useState } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Search, PenTool, Code, Rocket } from "lucide-react";
const stages = [{
  id: 1,
  title: "Discovery & Identification",
  description: "We leverage proprietary data models to identify undervalued software assets and strategic IP that align with our core holdings.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1624954/img/wireframe_discovery.png",
  icon: "search"
}, {
  id: 2,
  title: "Technical Due Diligence",
  description: "Exhaustive technical, financial, and legal audits ensure every acquisition meets our rigorous standards for security and scalability.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1624954/img/wireframe_design.png",
  icon: "pen"
}, {
  id: 3,
  title: "Integration & Smart-Contracting",
  description: "Seamlessly unifying assets into our digital ecosystem using robust smart-contracting frameworks and shared infrastructure.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1624954/img/wireframe_development.png",
  icon: "code"
}, {
  id: 4,
  title: "Strategic Expansion",
  description: "Applying our operational expertise and growth capital to accelerate performance, optimize systems, and dominate market share.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1624954/img/wireframe_launch.png",
  icon: "rocket"
}];
const techStacks = [{
  stageId: 1,
  tech: "Market Intelligence"
}, {
  stageId: 1,
  tech: "Asset Valuation"
}, {
  stageId: 1,
  tech: "Deal Flow"
}, {
  stageId: 2,
  tech: "Smart Contract Audit"
}, {
  stageId: 2,
  tech: "Risk Assessment"
}, {
  stageId: 2,
  tech: "IP Verification"
}, {
  stageId: 3,
  tech: "Unified API"
}, {
  stageId: 3,
  tech: "Blockchain Infrastructure"
}, {
  stageId: 3,
  tech: "Node Deployment"
}, {
  stageId: 4,
  tech: "Capital Injection"
}, {
  stageId: 4,
  tech: "Operational Scaling"
}, {
  stageId: 4,
  tech: "Market Dominance"
}];
const getIcon = (name: string) => {
  switch (name) {
    case "search":
      return <Search className="size-5" />;
    case "pen":
      return <PenTool className="size-5" />;
    case "code":
      return <Code className="size-5" />;
    case "rocket":
      return <Rocket className="size-5" />;
    default:
      return <Code className="size-5" />;
  }
};
function TimelineItem({
  stage,
  index,
  isEven
}: {
  stage: typeof stages[0];
  index: number;
  isEven: boolean;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.4,
    once: false
  });
  const stageTech = techStacks.filter(t => t.stageId === stage.id);
  return <div data-section-id="96840" ref={ref} data-index={index} className={`relative flex flex-col md:flex-row items-center justify-between w-full gap-8 md:gap-0 ${isEven ? 'md:flex-row-reverse' : ''}`}>
      {}
      <div className="absolute left-0 md:left-1/2 top-8 md:top-1/2 -translate-y-1/2 md:-translate-x-1/2 z-20 flex items-center justify-center">
        <div className={`size-10 rounded-full border-2 flex items-center justify-center transition-all duration-700 bg-background ${isVisible ? 'border-primary text-primary shadow-[0_0_20px_var(--color-primary)] scale-110' : 'border-muted-foreground/30 text-muted-foreground scale-100'}`}>
          {getIcon(stage.icon)}
        </div>
      </div>

      {}
      <div className={`w-full md:w-[45%] pl-12 md:pl-0 ${isEven ? 'md:pr-12 lg:pr-24' : 'md:pl-12 lg:pl-24'} transition-all duration-1000 delay-100 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
        <Card className={`bg-card/40 backdrop-blur-xl border-border/30 rounded-sm transition-colors duration-500 ${isVisible ? 'border-primary/30 shadow-[0_8px_30px_rgb(0,0,0,0.12)] shadow-primary/5' : ''}`}>
          <CardHeader>
            <div className="flex items-center gap-3 mb-2">
              <span className="text-primary font-mono text-sm font-bold tracking-wider">0{stage.id}</span>
              <div className="h-px flex-1 bg-border/50"></div>
            </div>
            <CardTitle className="text-2xl tracking-tight font-default">{stage.title}</CardTitle>
            <CardDescription className="text-base leading-relaxed mt-2 font-default">
              {stage.description}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2 mt-2">
              {stageTech.map((tech, i) => <Badge key={`${stage.id}-${tech.tech}`} data-index={i} variant="outline" className={`bg-background/50 border-border/50 font-mono text-[10px] transition-all duration-500 rounded-sm ${isVisible ? 'border-primary/40 text-primary-foreground bg-primary/10' : ''}`}>
                  {tech.tech}
                </Badge>)}
            </div>
          </CardContent>
        </Card>
      </div>

      {}
      <div className={`hidden md:flex w-full md:w-[45%] justify-center items-center transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
        <div className="relative w-full max-w-md aspect-video rounded-sm overflow-hidden border border-border/20 bg-muted/10 group">
          <div className={`absolute inset-0 bg-gradient-to-tr from-primary/20 to-transparent opacity-0 transition-opacity duration-700 ${isVisible ? 'opacity-100' : ''}`}></div>
          <img src={stage.image} alt={`${stage.title} illustration`} className={`w-full h-full object-cover mix-blend-screen opacity-70 transition-transform duration-1000 ${isVisible ? 'scale-105' : 'scale-100'}`} />
          {}
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]"></div>
        </div>
      </div>
    </div>;
}
export default function TheBlueprint() {
  const sectionRef = useRef<HTMLElement>(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const sectionTop = rect.top;
      const sectionHeight = rect.height;
      const startPoint = windowHeight / 2;
      const endPoint = -sectionHeight + windowHeight / 2;
      let progress = 0;
      if (sectionTop <= startPoint && sectionTop >= endPoint) {
        const totalScrollable = startPoint - endPoint;
        const currentScrolled = startPoint - sectionTop;
        progress = currentScrolled / totalScrollable * 100;
      } else if (sectionTop < endPoint) {
        progress = 100;
      }
      setScrollProgress(Math.max(0, Math.min(100, progress)));
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <section id="acquisition-lifecycle" ref={sectionRef} className="dark bg-background text-foreground relative py-24 md:py-32 overflow-hidden">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-primary/5 blur-[120px] rounded-full pointer-events-none"></div>
      <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-secondary/10 blur-[150px] rounded-full pointer-events-none"></div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        
        {}
        <div className="max-w-3xl mx-auto text-center mb-20 md:mb-32">
          <Badge variant="outline" className="mb-4 border-primary/50 text-primary bg-primary/10 backdrop-blur-sm px-4 py-1 text-sm font-mono rounded-sm">
            THE METHODOLOGY
          </Badge>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter mb-6 font-default">
            Acquisition <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">Lifecycle</span>
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground leading-relaxed font-default">
            Our disciplined framework for identifying, integrating, and scaling high-potential software ventures and digital infrastructure across global markets.
          </p>
        </div>

        {}
        <div className="relative max-w-6xl mx-auto">
          
          {}
          <div className="absolute left-[19px] md:left-1/2 top-0 bottom-0 w-px bg-border/40 -translate-x-1/2 z-0"></div>
          
          {}
          <div className="absolute left-[19px] md:left-1/2 top-0 w-[3px] bg-primary -translate-x-1/2 z-10 transition-all duration-100 ease-out shadow-[0_0_15px_var(--color-primary)]" style={{
          height: `${scrollProgress}%`
        }}>
            {}
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 size-3 bg-primary rounded-full shadow-[0_0_20px_4px_var(--color-primary)]"></div>
          </div>

          {}
          <div className="flex flex-col gap-16 md:gap-32 relative z-20">
            {stages.map((stage, index) => <TimelineItem key={stage.id} stage={stage} index={index} isEven={index % 2 !== 0} />)}
          </div>

        </div>
      </div>
    </section>;
}